import { useEffect, useMemo, useState } from "react";
import AppShell, { SectionCard } from "../components/AppShell";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, DownloadSimple, FileText, PencilSimple, Trash, Sparkle, ArrowClockwise, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CATS = ["offer","appointment","experience","relieving","noc","address_proof","salary_increment","warning","promotion","travel_authorization","other"];

export default function Letters() {
  const [tab, setTab] = useState("generated");
  const [pendingTplId, setPendingTplId] = useState(null);
  return (
    <AppShell title="Letters">
      <div className="flex items-center gap-1 mb-5 border-b border-zinc-200">
        {[{k:"generated",l:"Generated letters"},{k:"templates",l:"Templates"}].map(t => (
          <button key={t.k} onClick={()=>setTab(t.k)} data-testid={`letters-tab-${t.k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab===t.k?"border-zinc-950 text-zinc-950 font-medium":"border-transparent text-zinc-500 hover:text-zinc-900"}`}>{t.l}</button>
        ))}
      </div>
      {tab === "templates"
        ? <TemplatesTab onUseTemplate={(id) => { setPendingTplId(id); setTab("generated"); }}/>
        : <GeneratedTab pendingTplId={pendingTplId} clearPendingTpl={() => setPendingTplId(null)}/>}
    </AppShell>
  );
}

function TemplatesTab({ onUseTemplate }) {
  const [rows, setRows] = useState([]);
  const [manualOpen, setManualOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [f, setF] = useState(blank());
  const [editingId, setEditingId] = useState(null);

  function blank() { return { name:"", slug:"", category:"other", body_markdown:"", merge_fields:[] }; }

  const load = async () => { const r = await api.get("/letter-templates"); setRows(r.data); };
  useEffect(() => { load().catch(()=>{}); }, []);

  const openNew = () => { setF(blank()); setEditingId(null); setManualOpen(true); };
  const openEdit = (r) => { setF({name:r.name, slug:r.slug, category:r.category, body_markdown:r.body_markdown, merge_fields:r.merge_fields||[]}); setEditingId(r.id); setManualOpen(true); };

  const save = async () => {
    if (!f.name.trim() || !f.slug.trim() || !f.body_markdown.trim()) {
      toast.error("Name, slug and body are required.");
      return;
    }
    try {
      if (editingId) {
        await api.put(`/letter-templates/${editingId}`, f);
        toast.success("Template updated");
      } else {
        await api.post("/letter-templates", f);
        toast.success("Template saved");
      }
      setManualOpen(false); setF(blank()); setEditingId(null); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete template "${r.name}"? Existing generated letters will remain but this template will be hidden.`)) return;
    try {
      await api.delete(`/letter-templates/${r.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const useAIDraft = (draft) => {
    setF({
      name: draft.name || "",
      slug: draft.slug || "",
      category: draft.category || "other",
      body_markdown: draft.body_markdown || "",
      merge_fields: draft.merge_fields || [],
    });
    setEditingId(null);
    setAiOpen(false);
    setManualOpen(true);
  };

  return (
    <>
      <SectionCard title="Letter templates" subtitle="Markdown with {{merge_fields}} — e.g. {{employee_name}}, {{doj}}, {{ctc_annual}}, {{today}}. Use the AI wizard to draft quickly."
        action={
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={()=>setAiOpen(true)} data-testid="tmpl-ai-btn" className="gap-1.5">
              <Sparkle size={14} weight="bold"/> Draft with AI
            </Button>
            <Button size="sm" onClick={openNew} data-testid="tmpl-new-btn" className="gap-1.5">
              <Plus size={14} weight="bold"/> New template
            </Button>
          </div>
        }>
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead>Slug</TableHead><TableHead>Fields</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length===0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">No templates yet. Click <em>Draft with AI</em> to spin one up in seconds.</TableCell></TableRow>}
            {rows.map(r=>(
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="capitalize">{r.category?.replace(/_/g," ")}</TableCell>
                <TableCell className="text-zinc-500 text-xs">{r.slug}</TableCell>
                <TableCell className="text-xs text-zinc-500 max-w-xs truncate">{(r.merge_fields||[]).join(", ")}</TableCell>
                <TableCell className="text-right">
                  <div className="inline-flex gap-1">
                    <Button size="sm" variant="outline" className="gap-1" data-testid={`tmpl-use-${r.id}`} onClick={() => onUseTemplate?.(r.id)}>
                      <Plus size={12} weight="bold"/>Generate
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1" data-testid={`tmpl-edit-${r.id}`} onClick={()=>openEdit(r)}>
                      <PencilSimple size={12}/>
                    </Button>
                    <Button size="sm" variant="ghost" className="gap-1 text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`tmpl-del-${r.id}`} onClick={()=>del(r)}>
                      <Trash size={12}/>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      {/* Manual create/edit dialog */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-auto pr-1">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={f.name} onChange={e=>setF({...f, name:e.target.value})} data-testid="tmpl-form-name"/></div>
              <div><Label>Slug</Label><Input value={f.slug} onChange={e=>setF({...f, slug:e.target.value.toLowerCase().replace(/[^a-z0-9-]/g,"-")})} data-testid="tmpl-form-slug"/></div>
              <div className="col-span-2">
                <Label>Category</Label>
                <Select value={f.category} onValueChange={v=>setF({...f, category:v})}>
                  <SelectTrigger data-testid="tmpl-form-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATS.map(c=>(<SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g," ")}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Body (Markdown with {"{{merge}}"} fields)</Label>
              <Textarea rows={14} value={f.body_markdown} onChange={e=>setF({...f, body_markdown:e.target.value})} data-testid="tmpl-form-body" placeholder={`To whom it may concern,\n\nThis is to certify that {{employee_name}} ({{employee_code}}) has been employed with us as {{designation}} from {{doj}}.\n\nDate: {{today}}`}/>
              <p className="text-xs text-zinc-500 mt-1">Common: employee_name, employee_code, designation, department, branch, doj, ctc_annual, gross_monthly, today, signatory_name, signatory_title.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setManualOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="tmpl-form-save">{editingId ? "Save changes" : "Save template"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI wizard */}
      <AIDraftDialog open={aiOpen} onOpenChange={setAiOpen} onAccept={useAIDraft}/>
    </>
  );
}

function AIDraftDialog({ open, onOpenChange, onAccept }) {
  const [step, setStep] = useState(1);
  const [brief, setBrief] = useState("");
  const [tone, setTone] = useState("professional and warm");
  const [category, setCategory] = useState("other");
  const [extraFields, setExtraFields] = useState("");
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState(null);
  const [refinement, setRefinement] = useState("");

  useEffect(() => { if (open) { setStep(1); setBrief(""); setDraft(null); setRefinement(""); } }, [open]);

  const generate = async (isRefine=false) => {
    if (!brief.trim() && !isRefine) { toast.error("Tell me what letter you need."); return; }
    setBusy(true);
    try {
      const payload = {
        brief,
        category,
        tone,
        include_fields: extraFields.split(",").map(s=>s.trim()).filter(Boolean),
      };
      if (isRefine) { payload.existing_body = draft.body_markdown; payload.refinement = refinement; }
      const { data } = await api.post("/letter-templates/ai-draft", payload);
      setDraft(data);
      setStep(2);
      setRefinement("");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "AI draft failed.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Sparkle size={18} weight="fill" className="text-amber-500"/> Draft a letter with AI</DialogTitle></DialogHeader>

        {step === 1 && (
          <div className="space-y-3 py-2">
            <div>
              <Label>What letter do you need? Describe it in plain English.</Label>
              <Textarea rows={5} value={brief} onChange={e=>setBrief(e.target.value)} data-testid="ai-brief"
                placeholder={`e.g. "An offer letter for a Senior Sales Manager with 3-month probation, ₹18L CTC, joining next month, based in Mumbai."`}/>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger data-testid="ai-cat"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATS.map(c=>(<SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g," ")}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="professional and warm">Professional & warm</SelectItem>
                    <SelectItem value="formal and legal">Formal & legal</SelectItem>
                    <SelectItem value="friendly and casual">Friendly & casual</SelectItem>
                    <SelectItem value="firm and direct">Firm & direct</SelectItem>
                    <SelectItem value="celebratory">Celebratory</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Extra merge fields (comma-sep, optional)</Label>
                <Input value={extraFields} onChange={e=>setExtraFields(e.target.value)} placeholder="probation_months, joining_bonus"/>
              </div>
            </div>
            <p className="text-xs text-zinc-500">AI will draft the letter with <code>&#123;&#123;merge_fields&#125;&#125;</code>. You&apos;ll review and can refine before saving.</p>
          </div>
        )}

        {step === 2 && draft && (
          <div className="space-y-3 py-2 max-h-[65vh] overflow-auto pr-1">
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Name</Label><Input value={draft.name} onChange={e=>setDraft({...draft, name:e.target.value})}/></div>
              <div><Label>Slug</Label><Input value={draft.slug} onChange={e=>setDraft({...draft, slug:e.target.value})}/></div>
              <div>
                <Label>Category</Label>
                <Select value={draft.category} onValueChange={v=>setDraft({...draft, category:v})}>
                  <SelectTrigger><SelectValue/></SelectTrigger>
                  <SelectContent>{CATS.map(c=>(<SelectItem key={c} value={c} className="capitalize">{c.replace(/_/g," ")}</SelectItem>))}</SelectContent>
                </Select>
              </div>
            </div>
            {draft.ai_notes && (
              <div className="text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-900">
                <b>AI notes:</b> {draft.ai_notes}
              </div>
            )}
            <div>
              <Label>Draft (editable)</Label>
              <Textarea rows={14} value={draft.body_markdown} onChange={e=>setDraft({...draft, body_markdown:e.target.value})} data-testid="ai-body"/>
              <div className="text-xs text-zinc-500 mt-1">
                Detected merge fields: {(draft.merge_fields || []).map(f => <code key={f} className="mx-0.5 px-1 py-0.5 bg-zinc-100 rounded">{`{{${f}}}`}</code>)}
              </div>
            </div>
            <div>
              <Label>Not quite right? Tell AI what to change:</Label>
              <div className="flex gap-2">
                <Input value={refinement} onChange={e=>setRefinement(e.target.value)} placeholder={`e.g. "Add a confidentiality clause" or "Shorten by half"`} data-testid="ai-refine"/>
                <Button variant="outline" onClick={()=>generate(true)} disabled={busy || !refinement.trim()} className="gap-1.5" data-testid="ai-refine-btn">
                  <ArrowClockwise size={14}/> Refine
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 1 ? (
            <>
              <Button variant="outline" onClick={()=>onOpenChange(false)}>Cancel</Button>
              <Button onClick={()=>generate(false)} disabled={busy} className="gap-1.5" data-testid="ai-generate-btn">
                <Sparkle size={14} weight="bold"/>{busy ? "Drafting…" : "Draft with AI"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={()=>setStep(1)}>Back</Button>
              <Button onClick={()=>onAccept(draft)} className="gap-1.5" data-testid="ai-accept-btn">
                <CheckCircle size={14} weight="bold"/> Looks good — continue
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GeneratedTab({ pendingTplId, clearPendingTpl }) {
  const [rows, setRows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [tplId, setTplId] = useState("");
  const [empId, setEmpId] = useState("");

  const load = async () => {
    try {
      const [letters, tpls, emps] = await Promise.all([api.get("/letters"), api.get("/letter-templates"), api.get("/employees")]);
      setRows(letters.data); setTemplates(tpls.data); setEmployees(emps.data);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (pendingTplId) { setTplId(pendingTplId); setOpen(true); clearPendingTpl?.(); }
  }, [pendingTplId, clearPendingTpl]);

  const generate = async () => {
    try { await api.post("/letters/generate", { template_id: tplId, employee_id: empId });
      toast.success("Letter generated"); setOpen(false); setTplId(""); setEmpId(""); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const download = async (id, name) => {
    try {
      const r = await api.get(`/letters/${id}/pdf`, { responseType:"blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href=url; link.download=name+".pdf";
      document.body.appendChild(link); link.click(); link.remove();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  return (
    <>
      <SectionCard title="Generated letters" subtitle="Render, e-sign, and download PDF. Your uploaded letterhead + signature appear automatically."
        action={<Button size="sm" onClick={()=>setOpen(true)} data-testid="letter-gen-btn" className="gap-1.5"><Plus size={14} weight="bold"/> Generate</Button>}>
        <Table>
          <TableHeader><TableRow><TableHead>Template</TableHead><TableHead>Employee</TableHead><TableHead>Status</TableHead><TableHead>Issued</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.length===0 && <TableRow><TableCell colSpan={5} className="text-center text-zinc-500 py-6">No letters yet.</TableCell></TableRow>}
            {rows.map(r=>(
              <TableRow key={r.id}>
                <TableCell className="font-medium flex items-center gap-2"><FileText size={14}/>{r.template_name}</TableCell>
                <TableCell>{r.employee_name || "-"}</TableCell>
                <TableCell><Badge className={r.status==="signed"?"bg-emerald-100 text-emerald-800":"bg-zinc-100 text-zinc-700"}>{r.status}</Badge></TableCell>
                <TableCell className="text-zinc-500 text-xs">{(r.issued_at||"").slice(0,10)}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="ghost" onClick={()=>download(r.id, `${r.template_name}_${r.employee_name||"letter"}`)} className="gap-1"><DownloadSimple size={14}/>PDF</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </SectionCard>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle>Generate letter</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <Label>Template</Label>
              <Select value={tplId} onValueChange={setTplId}><SelectTrigger><SelectValue placeholder="Select template"/></SelectTrigger><SelectContent>{templates.map(t=>(<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}</SelectContent></Select>
            </div>
            <div>
              <Label>Employee (optional)</Label>
              <Select value={empId} onValueChange={setEmpId}><SelectTrigger><SelectValue placeholder="Select employee"/></SelectTrigger><SelectContent>{employees.map(e=>(<SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>))}</SelectContent></Select>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={()=>setOpen(false)}>Cancel</Button><Button onClick={generate}>Generate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
